/**
 * Sammel-Upload (MP-E, Laptop zuerst): viele Fotos auf einmal ablegen → eine Zeile je Foto mit Bezeichnung, Preis, Kategorie;
 * oder eine Liste einfügen („Bezeichnung; Preis; Kategorie“ je Zeile). Dann alles auf einmal anlegen, Fotos nachladen.
 */
import { useRef, useState } from "react";
import { Link } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { fotoVerkleinern } from "./foto";
import { useArtikelAnlegen, useErfassenInfo, useFotoHochladen } from "./artikel-api";
import { useCockpit } from "./cockpit-api";
import { useT, useTx } from "../i18n";

interface Zeile {
  id: string;
  bezeichnung: string;
  preis: string;
  kategorie: string;
  groesse: string;
  foto: { blob: Blob; url: string } | null;
  status: "offen" | "laeuft" | "fertig" | "fehler";
  fehler?: string;
}

function neueZeile(teil: Partial<Zeile> = {}): Zeile {
  return { id: crypto.randomUUID(), bezeichnung: "", preis: "", kategorie: "", groesse: "", foto: null, status: "offen", ...teil };
}

export function Sammel() {
  const tx = useTx();
  const t = useT();
  const info = useErfassenInfo();
  const ck = useCockpit();
  const anlegen = useArtikelAnlegen();
  const fotoHoch = useFotoHochladen();
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [regalId, setRegalId] = useState("");
  const [liste, setListe] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [ziehen, setZiehen] = useState(false);
  const dateien = useRef<HTMLInputElement>(null);
  const kategorien = ck.data?.einstellungen.kategorien ?? [];

  async function fotosAufnehmen(files: FileList | File[]) {
    const neu: Zeile[] = [];
    for (const f of Array.from(files).filter((x) => x.type.startsWith("image/")).slice(0, 50)) {
      try {
        const blob = await fotoVerkleinern(f);
        neu.push(neueZeile({ bezeichnung: f.name.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").trim().slice(0, 80), foto: { blob, url: URL.createObjectURL(blob) } }));
      } catch {
        neu.push(neueZeile({ bezeichnung: f.name, status: "fehler", fehler: "Bild unlesbar" }));
      }
    }
    setZeilen((z) => [...z, ...neu]);
  }

  function listeUebernehmen() {
    const neu = liste
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [b = "", p = "", k = "", g = ""] = l.split(/[;\t]/).map((x) => x.trim());
        return neueZeile({ bezeichnung: b.slice(0, 80), preis: p.replace("€", "").trim(), kategorie: k.slice(0, 40), groesse: g.slice(0, 20) });
      });
    setZeilen((z) => [...z, ...neu]);
    setListe("");
  }

  const aendern = (id: string, teil: Partial<Zeile>) => setZeilen((z) => z.map((x) => (x.id === id ? { ...x, ...teil } : x)));
  const offen = zeilen.filter((z) => z.status !== "fertig");
  const bereit = offen.filter((z) => z.bezeichnung.trim() && (parseEuro(z.preis) ?? 0) > 0);

  async function alleAnlegen() {
    setLaeuft(true);
    for (const z of zeilen) {
      if (z.status === "fertig") continue;
      const preisCent = parseEuro(z.preis);
      if (!z.bezeichnung.trim() || !preisCent || preisCent <= 0) {
        aendern(z.id, { status: "fehler", fehler: "Bezeichnung oder Preis fehlt" });
        continue;
      }
      aendern(z.id, { status: "laeuft", fehler: undefined });
      try {
        const r = await anlegen.mutateAsync({ bezeichnung: z.bezeichnung.trim(), preisCent, kategorie: z.kategorie.trim(), groesse: z.groesse.trim(), anzahl: 1, regalId: regalId || undefined });
        if (z.foto && r.artikel[0]) {
          try {
            await fotoHoch.mutateAsync({ id: r.artikel[0].id, blob: z.foto.blob });
          } catch {
            // Artikel ist da, Foto fehlt – lässt sich im Artikel nachholen
          }
        }
        aendern(z.id, { status: "fertig" });
      } catch (e) {
        aendern(z.id, { status: "fehler", fehler: e instanceof ApiFehler ? e.message : "Anlegen hat nicht geklappt" });
      }
    }
    setLaeuft(false);
  }

  const fertig = zeilen.filter((z) => z.status === "fertig").length;

  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <div className="sc-row">
        <Link to="/mieter/erfassen" className="rg-btn rg-btn--quiet" aria-label={t("zurueck")}>‹</Link>
        <h1 className="title">{t("kachel.sammel")}</h1>
      </div>
      <p className="sc-muted">{tx("Am Laptop oder Tablet: viele Fotos auf einmal ablegen, dann nur noch Bezeichnung und Preis je Zeile. Oder eine Liste einfügen.")}</p>
      <Seitenzustand query={info} lines={3}>
        {(d) => (
          <>
            {d.regale.length > 1 ? (
              <Select label="Regal" value={regalId} onChange={(e) => setRegalId(e.target.value)}>
                <option value="">Automatisch (erstes Regal)</option>
                {d.regale.map((r) => <option key={r.regalId} value={r.regalId}>Regal {r.nummer}</option>)}
              </Select>
            ) : null}
            <div
              className={`mp-ablage${ziehen ? " is-aktiv" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
              onDragLeave={() => setZiehen(false)}
              onDrop={(e) => { e.preventDefault(); setZiehen(false); void fotosAufnehmen(e.dataTransfer.files); }}
              role="button"
              tabIndex={0}
              onClick={() => dateien.current?.click()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && dateien.current?.click()}
              aria-label="Fotos ablegen oder auswählen"
            >
              <span className="sc-strong">Fotos hier ablegen</span>
              <span className="sc-muted sc-small">oder antippen und auswählen – bis zu 50 auf einmal</span>
              <input ref={dateien} type="file" accept="image/*" multiple hidden onChange={(e) => { void fotosAufnehmen(e.target.files ?? []); e.target.value = ""; }} />
            </div>
            <details>
              <summary className="heading" style={{ cursor: "pointer" }}>Liste einfügen (ohne Fotos)</summary>
              <textarea className="sc-textarea" aria-label="Liste" rows={5} value={liste} onChange={(e) => setListe(e.target.value)} placeholder={"Je Zeile: Bezeichnung; Preis; Kategorie\nBlaue Vase; 8,00; Deko\nKinderjacke Gr. 110; 12; Kleidung"} />
              <Button variant="secondary" icon="plus" onClick={listeUebernehmen} disabled={!liste.trim()}>{tx("Zeilen übernehmen")}</Button>
            </details>
          </>
        )}
      </Seitenzustand>

      {zeilen.length ? (
        <Card title={`${zeilen.length} ${zeilen.length === 1 ? "Zeile" : "Zeilen"}`} actions={<StatusBadge status={fertig === zeilen.length ? "paid" : "neutral"}>{fertig} angelegt</StatusBadge>}>
          <div className="mp-sammel">
            {zeilen.map((z) => (
              <div key={z.id} className={`mp-sammel__zeile is-${z.status}`}>
                {z.foto ? <img className="rg-thumb" src={z.foto.url} alt="" /> : <span className="rg-thumb rg-thumb--empty" aria-hidden="true" />}
                <TextField label="Bezeichnung" value={z.bezeichnung} onChange={(e) => aendern(z.id, { bezeichnung: e.target.value })} disabled={z.status === "fertig"} maxLength={80} />
                <TextField label="Preis" value={z.preis} onChange={(e) => aendern(z.id, { preis: e.target.value })} inputMode="decimal" suffix="€" disabled={z.status === "fertig"} />
                <TextField label="Kategorie" value={z.kategorie} onChange={(e) => aendern(z.id, { kategorie: e.target.value })} list="mp-sammel-kategorien" disabled={z.status === "fertig"} maxLength={40} />
                <div className="sc-row" style={{ alignSelf: "end" }}>
                  {z.status === "fertig" ? <StatusBadge status="paid">Angelegt</StatusBadge> : z.status === "fehler" ? <StatusBadge status="attention">{z.fehler ?? "Fehler"}</StatusBadge> : z.status === "laeuft" ? <StatusBadge status="neutral">…</StatusBadge> : <Button variant="quiet" icon="x" aria-label="Zeile entfernen" onClick={() => setZeilen((l) => l.filter((x) => x.id !== z.id))} />}
                </div>
              </div>
            ))}
          </div>
          <datalist id="mp-sammel-kategorien">{kategorien.map((k) => <option key={k} value={k} />)}</datalist>
          {fertig === zeilen.length ? (
            <Notice tone="joy" icon="sparkles" title={tx("Alles angelegt")}>{zeilen.length} Artikel sind im Portal. <Link to="/mieter/etiketten">Etiketten drucken</Link> und ab ins Regal.</Notice>
          ) : (
            <Button variant="primary" block icon="check" loading={laeuft} disabled={!bereit.length} onClick={() => void alleAnlegen()}>
              {bereit.length} {bereit.length === 1 ? "Artikel" : "Artikel"} anlegen{bereit.length ? ` · ${formatEuro(bereit.reduce((a, z) => a + (parseEuro(z.preis) ?? 0), 0))}` : ""}
            </Button>
          )}
        </Card>
      ) : null}
    </div>
  );
}
